import * as React from 'react'
import {
  findFirstEnabledIndex,
  resolveNextHighlightIndex,
} from '../helpers/zoom-highlight-navigation'
import { isSkippedZoomOption } from '../helpers/zoom-menu-options'
import type { ZoomMenuOption } from '../types/zoom-menu-option'

type UseZoomHighlightArgs = {
  open: boolean
  menuOptions: ZoomMenuOption[]
  zoomSelectionRowDisabled: boolean
  fitToSpreadDisabled: boolean
  applyHighlightPreview: (index: number) => void
  listRef: React.RefObject<HTMLDivElement | null>
}

/** The active (`aria-activedescendant`) option: keyboard/hover highlight within the open list. */
export function useZoomHighlight({
  open,
  menuOptions,
  zoomSelectionRowDisabled,
  fitToSpreadDisabled,
  applyHighlightPreview,
  listRef,
}: UseZoomHighlightArgs) {
  const [highlightIndex, setHighlightIndex] = React.useState(-1)
  const wasMenuOpenRef = React.useRef(false)

  const isKeyboardSkippedOptionIndex = React.useCallback(
    (index: number) =>
      isSkippedZoomOption(menuOptions[index], { zoomSelectionRowDisabled, fitToSpreadDisabled }),
    [menuOptions, zoomSelectionRowDisabled, fitToSpreadDisabled]
  )

  React.useEffect(() => {
    if (!open) {
      setHighlightIndex(-1)
    }
  }, [open])

  /**
   * When the menu opens, set list highlight to the first valid option so `aria-activedescendant`
   * targets a real `id` for SRs. Intentionally does not call `applyHighlightPreview` here so an
   * in-progress typed zoom value is not overwritten on open.
   */
  React.useEffect(() => {
    if (open) {
      if (!wasMenuOpenRef.current) {
        const first = findFirstEnabledIndex(menuOptions.length, isKeyboardSkippedOptionIndex)

        if (first >= 0) {
          setHighlightIndex(first)
        }
      }

      wasMenuOpenRef.current = true
    } else {
      wasMenuOpenRef.current = false
    }
  }, [open, menuOptions, isKeyboardSkippedOptionIndex])

  React.useEffect(() => {
    if (!open || highlightIndex < 0) return

    const root = listRef.current
    const el = root?.querySelector<HTMLElement>(`[data-zoom-option-index="${highlightIndex}"]`)

    el?.scrollIntoView({ block: 'nearest' })
  }, [highlightIndex, open, listRef])

  const moveHighlight = (delta: number) => {
    const n = menuOptions.length

    if (n === 0) return

    setHighlightIndex((prev) => {
      const next = resolveNextHighlightIndex(prev, delta, n, isKeyboardSkippedOptionIndex)

      if (next == null) {
        return prev
      }

      applyHighlightPreview(next)

      return next
    })
  }

  /** Pointer hover: highlight the row and preview it. */
  const highlightOption = (index: number) => {
    setHighlightIndex(index)
    applyHighlightPreview(index)
  }

  return { highlightIndex, setHighlightIndex, moveHighlight, highlightOption }
}
